<template>
  <div
    ref="modal"
    data-test="us-modal"
    class="us-modal"
    :class="[
      { 'us-modal--subscreen': !fullscreen },
      { 'us-modal--slot-overflow-visible': slotOverflowVisible }
    ]"
  >
    <div class="us-modal__backdrop"></div>
    <div class="us-modal__content-container">
      <div class="us-modal__content">
        <button v-if="showClose" ref="close" class="us-modal__close">
          <USIcon
            name="x"
            color="var(--color--primary-b1)"
            css-diameter="var(--closeIconDiameter)"
          />
        </button>
        <div class="us-modal__content--slot">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, watch, ref, onUnmounted } from 'vue'
import { Lightbox } from '@base/@motiv/fe-patterns'

import USIcon from '@base/components/USIcon/USIcon.vue'

export default defineComponent({
  name: 'USModal',
  components: { USIcon },
  props: {
    fullscreen: { type: Boolean, default: false },
    showClose: { type: Boolean, default: true },
    isOpen: { type: Boolean, default: false },
    enableKeyboard: { type: Boolean, default: true },
    slotOverflowVisible: { type: Boolean, default: false }
  },
  emits: ['update:isOpen'],
  setup(props, { emit }) {
    const modal = ref<HTMLElement | undefined>(undefined)
    const close = ref<HTMLElement | undefined>(undefined)
    let lightbox: Lightbox | undefined

    onMounted(() => {
      if (!modal.value) return
      lightbox = new Lightbox(
        modal.value,
        undefined,
        close.value,
        props.enableKeyboard
      )

      lightbox.init()
      if (props.isOpen) lightbox.open()

      watch(
        () => props.isOpen,
        (newValue) => {
          if (!lightbox) return
          if (newValue) lightbox.open()
          if (!newValue) lightbox.close()
        }
      )

      lightbox.on('toggle', (e: boolean) => {
        emit('update:isOpen', e)
      })
    })

    onUnmounted(() => {
      if (lightbox) {
        lightbox.destroy()
        lightbox = undefined
      }
    })

    return { modal, close }
  }
})
</script>

<style lang="scss">
@use '@base/@motiv/fe-patterns/styles/lightbox' as *;

body {
  @include lightbox-body;
}
</style>

<style lang="scss" scoped>
@use '@base/@motiv/fe-patterns/styles/lightbox' as *;

@use '@base/styles/v1.0/scss/foundations/breakpoint';
@use '@base/styles/v1.0/scss/foundations/motion';

@include lightbox('.us-modal', 0.3s);

.us-modal {
  align-items: center;
  display: flex;
  height: 100vh;
  justify-content: center;
  pointer-events: none;
  // prettier-ignore
  transition:
    opacity motion.duration(1) ease-out,
    visibility 0s ease-out motion.delay(1),
    z-index 0s linear motion.delay(1);

  &.show {
    pointer-events: auto;
  }

  &__backdrop {
    backdrop-filter: blur(10px);
    background-color: rgba(45, 45, 45, 0.4);
    height: 100%;
    position: absolute;
    width: 100%;
  }

  &__content-container {
    bottom: 0;
    left: 0;
    overflow-y: scroll;
    position: absolute;
    right: 0;
    top: 0;

    .us-modal--subscreen & {
      @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
        padding: 10% 0;
      }
    }
  }

  &__content {
    align-items: center;
    background: var(--color--neutral-a5);
    display: flex;
    height: auto;
    margin: 0 auto;
    min-height: 100%;
    padding: var(--spacing--3) var(--spacing--4);
    width: 100%;

    .us-modal--subscreen & {
      @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
        border-radius: var(--border-radius--small);
        overflow: hidden;
        height: fit-content;
        max-width: breakpoint.$breakpoint--9;
        min-height: auto;
        width: 80%;
      }

      @include breakpoint.min-width(breakpoint.$breakpoint--desktop) {
        width: 70%;
      }
    }

    &--slot {
      margin: 0 auto;
      max-width: breakpoint.$breakpoint--9;
      overflow: auto;
      width: 100%;
    }
  }

  &__close {
    --closeIconDiameter: 20px;
    cursor: pointer;
    position: absolute;
    right: var(--spacing--7);
    top: var(--spacing--7);
    z-index: 2;

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      --closeIconDiameter: 24px;
    }
  }

  &--slot-overflow-visible {
    .us-modal__content {
      overflow: scroll;

      &--slot {
        overflow: visible;
      }
    }
  }
}
</style>

<!-- TODO
- update motion to new
-->
